/* Copyright (c) 2021 MOCA Systems, Inc.  All rights reserved. */

import { Injectable } from '@angular/core';
import { AbstractControl, UntypedFormGroup } from '@angular/forms';
import {
	ActualPhase,
	applyVarianceToPin,
	clonePin,
	createInitialPinData,
	plannedOnly,
	Side,
	Ticket,
	TicketType,
} from '@touchplan/common';
import { DynamicFlagTypes } from '@touchplan/feature-flags';
import { ILoggerService } from '@touchplan/logging';
import * as strings from '@touchplan/strings';
import { BehaviorSubject, combineLatest, concat, defer, EMPTY, Observable, of, race, Subject } from 'rxjs';
import {
	bufferTime,
	debounceTime,
	distinctUntilChanged,
	endWith,
	filter,
	map,
	startWith,
	switchMap,
	take,
	takeUntil,
	tap,
} from 'rxjs/operators';
import { TicketActionTypes } from 'src/app/app-data/actions/ticket-actions';
import {
	CombinedTicketTypes,
	EditTicketsCombined,
	TicketDropdownTypes,
} from 'src/app/app-data/models/EditTicketsCombined';
import { TicketList } from 'src/app/common/fancy-firebase-ticket/lists/TicketList';
import { LoggerServiceFactory } from 'src/app/common/logging/LoggerServiceFactory';
import { analyticsEventsService } from 'src/app/plain-javascript/services/analytics/AnalyticsEventsService';
import { clipboardManager } from 'src/app/plain-javascript/services/ClipboardManager';
import { FeatureFlagManager } from 'src/app/services/FeatureFlagManager';
import { PlanState, StateEvents } from 'src/app/services/plan-state.service';
import { PlanAccessCheck } from 'src/app/services/PlanAccessCheck';
import { ValidatedPlanActions } from 'src/app/services/validated-plan-actions.service';
import { PersistentEditData } from './interfaces/PresistentEditData';
import { TemplateType } from './interfaces/TemplateType';
import { TicketEditServiceTemplater } from './ticket-edit-templater.service';

export enum EditOverlay {
	procoreLink = 'procoreLink',
	procoreStatus = 'procoreStatus',
	pinHistory = 'pinHistory',
	constraintRoleSelector = 'constraintRoleSelector',
}

const VERT_PIXEL_OFFSET_FROM_CENTER_ON_TICKET_EDIT_FOCUS = 62;

// actual thinkings
// - setting duration, dates, possibly changing ticket workdays wil need to trigger other fields to update
// -- do this as part of the writeCombinationBackToList method, since patching localUpdate would cause cause those
// things to always be tied together, and they probably shouldn't
// -- since this sort of thing would essentially be a form field updating a different form field, the
// solution to suppress form updates is probably insufficient
// -- live date math and such
// -- blindly persist combinedEdit derived data
//
// - the "modes" of actualization should be stored as a property on each ticket (autocalculated by the data)
// -- probably enum it
// -- will need to autoupdate and be specifically settable
// --- maybe something like:
// ---- -on database change, set to planned or actualized- skip if necessary
// ---- on change to edit automatically update to planned or actualized based on actualFinish
// ---- on actualization function being called from edit service (it's a glorified live update) it should specically set to actualizing
// ---- on clicking the edit actuals button, it should specically set to actualizing
// ---- when reverting edit to planned, set to planned
// -- how to handle multi-edit, probably start by mapping the mode property the same way as every other property
// and just assume that since multi won't call the actualization function it won't be an issue
//
//
// from clicking the actualize button
// - call actualize function on ticketEditService
// - function should do validation, call date math code, and write to the updatelocals action
// -- the combinedEdit$ observable operates async on initial open (for the debounce) but _should_ operate syncronously for updates
// --- this means that we should be able to safely persist the form data to the tickets, then rely on the tickets
// updating the combined edit, so the actualize function can operate on the current set of combinedEdit data
// -- <------------ now fill out these details
// - as a result of that write, the mode key should be updated to the actulizing state
// - component code will check the mode key and display menu values and disable fields based on that

//set of edit data, PersistentEditData, that is not rebuilt on every update but instead used to maintain
// local data/ state during a whole edit session

//next things
// - fix the ticketEdit component (it should just use the keys as is...)
// - add update handling system (after the initial data from editTicket$)
// - get better ticket data merging and type the result

@Injectable()
export class TicketEditService {
	/** access the syncronous value of isEditValid, for instance checks */
	public get isEditValidSync() {
		return this.isEditValid$.getValue();
	}

	public closeEditAndCheckDirty$ = new Subject<boolean>();

	/** runs on plan destruction */
	private shutdownSubject = new Subject<void>();
	/** called to restart the editTickets watcher */
	private editClosedSubject = new Subject();
	/** tickets vs floating tickets */
	private selectedTicketList: TicketList;

	private previousHudPos: { x: number; y: number; z: number } = null;

	public ticketEditPanelIsDisplayed$: BehaviorSubject<boolean> = new BehaviorSubject(false);

	//new stuff
	public combinedEdit$ = new BehaviorSubject<EditTicketsCombined | undefined>(undefined);
	//there was no reason for this to be public, get your type from combinedEdit$
	private currentType$ = new BehaviorSubject<CombinedTicketTypes>(TicketType.Task);
	public combinedEditForForm$: Observable<EditTicketsCombined>;
	public editOverlay$ = new BehaviorSubject<EditOverlay>(null);
	public workdays$ = new BehaviorSubject([]);

	public externalSaveCustomFieldsRequest$ = new Subject<void>();

	/** emits with a close type right before closing, should only check for "save". Cancel can be considered the default. */
	public closeMethod$ = new Subject<'save' | 'cancel'>();

	//TODO for later, add customiziation by combining in an override observable
	public ticketTemplate$: Observable<TemplateType | undefined | null> = this.combinedEdit$.pipe(
		map((etc) => {
			if (!etc) {
				return null;
			}
			if (etc.originalList.size === 0) {
				return null;
			}

			const isConstraintType =
				etc.selectedType === TicketType.Constraint ||
				etc.selectedType === TicketDropdownTypes.CONSTRAINT_ACCEPTED ||
				etc.selectedType === TicketDropdownTypes.CONTRAINT_ASSIGNED;

			if (etc.originalList.size > 1) {
				if (isConstraintType) {
					return TemplateType.multiConstraint;
				}
				if (etc.selectedType === TicketType.Milestone) {
					return TemplateType.multiMilestone;
				}
				if (etc.selectedType === TicketType.Task) {
					return TemplateType.multiTask;
				}
			} else {
				if (isConstraintType) {
					return TemplateType.constraint;
				}
				if (etc.selectedType === TicketType.Milestone) {
					return TemplateType.milestone;
				}
				if (etc.selectedType === TicketType.Task) {
					return TemplateType.task;
				}
			}
		}),
		// tap(thing => this.logger.log('ticketTemplate', thing)),
		distinctUntilChanged()
	);

	private _editForm: UntypedFormGroup;
	private _editformKilled = new Subject();
	/** subscribe to the validity of edit. Currently just tied to the edit form */
	public isEditValid$ = new BehaviorSubject(true);

	private persistentData$ = new BehaviorSubject<PersistentEditData>(this.resetPersistentData());

	//only one thing can subscribe to this, don't use
	private internalTypeUpdateDisabled = false;
	private internalType$ = this.currentType$.pipe(
		switchMap((type) => {
			if (this.internalTypeUpdateDisabled) {
				this.internalTypeUpdateDisabled = false;
				return EMPTY;
			} else {
				return of(type);
			}
		})
	) as Observable<TicketType>;

	//TODO - don't like this, try and remove, store this state in the observable, or at least in the EditTicketsCombined model
	public isEditing = false;
	private logger: ILoggerService;

	constructor(
		private planState: PlanState,
		private ticketEditServiceTemplater: TicketEditServiceTemplater,
		private validatedPlanActions: ValidatedPlanActions,
		private featureFlagManager: FeatureFlagManager,
		private planAccessCheck: PlanAccessCheck
	) {
		this.logger = LoggerServiceFactory.create('TicketEditService');
		// window.editService = this;

		planState.stateChange$.subscribe((stateEvent: StateEvents) => {
			switch (stateEvent) {
				case StateEvents.INITIALIZED:
					this.generateEditWatcher();
					break;
				case StateEvents.BOOMED:
					this.releasePlanData();
					break;
				default:
					break;
			}
		});
	}

	/**
	 * File attach is enabled only when two conditions are satisfied:
	 * (1) The feature flag, "fileAttachEnabled", is true, and
	 * (2) Project level configuration is not false.  That is, if a project
	 *     has a boolean "fileAttachEnabled" flag set to false, file attach
	 *     for that project is disabled.  But if that flag is true, OR if
	 *     that flag isn't set for a project, file attach is enabled for
	 *     that project
	 */
	public get fileAttachEnabled$(): Observable<boolean> {
		return combineLatest([
			this.planState.project?.object$.pipe(
				filter((project) => !!project),
				map((project) => project.fileAttachEnabled ?? true) // true is actually the default
			),
			this.featureFlagManager.getFlag$(DynamicFlagTypes.FILE_ATTACH_ENABLED),
		]).pipe(map((value) => value[0] && value[1]));
	}

	private noEmitTypeUpdate(type: TicketType) {
		this.internalTypeUpdateDisabled = true;
		this.currentType$.next(type);
	}

	//get a new set
	private resetPersistentData(): PersistentEditData {
		return {
			// clearedKeys: new Map()
		};
	}

	// private initPlanData(){
	// 	this.combinedEdit$ = new BehaviorSubject<EditTicketsCombined>(undefined);
	// 	this.combinedEdit$
	// }

	private releasePlanData() {
		//reset back to defaults

		// this.currentType$.next(TicketType.TASK);
		// this.editOverlay$.next(null);
		this.combinedEdit$.next(EditTicketsCombined.EMPTY());
		this.hideTicketAttachments();

		this.shutdownSubject.next();
	}
	private generateEditWatcher() {
		const selectTicketList = concat(
			of(true), //always start listening once
			this.editClosedSubject
		).pipe(
			switchMap((__) => {
				//restart the race when the ticket is closed
				// return race(
				// 	this.planState.tickets.editTicket$.pipe(filter(res => this.isEditing || (res && res.size > 0)), debounceTime(10),
				// 		tap(f => this.selectedTicketList = this.planState.tickets)),
				// 	this.planState.floatingTickets.editTicket$.pipe(filter(res => this.isEditing || (res && res.size > 0)), debounceTime(10),
				// 		tap(f => this.selectedTicketList = this.planState.floatingTickets)),
				// );
				return race(
					this.planState.tickets.editTicket$.pipe(
						debounceTime(10),
						filter((res) => this.isEditing || (res && res.size > 0)),
						tap((f) => (this.selectedTicketList = this.planState.tickets))
					),
					this.planState.floatingTickets.editTicket$.pipe(
						debounceTime(10),
						filter((res) => this.isEditing || (res && res.size > 0)),
						tap((f) => (this.selectedTicketList = this.planState.floatingTickets))
					)
				);
			}),
			takeUntil(this.shutdownSubject)
			//tap(thing => this.logger.log('selectTicketList end'))
		);

		const editOb$ = combineLatest([
			//this.planState.tickets.editTicket$.pipe(debounceTime(10)),
			selectTicketList,
			this.internalType$.pipe(takeUntil(this.shutdownSubject)),
			this.persistentData$.pipe(takeUntil(this.shutdownSubject)),
		]).pipe(
			map((stuff) => {
				let editData: EditTicketsCombined;
				if (!stuff[0]) {
					editData = null;
				} else {
					editData = new EditTicketsCombined(stuff[1], stuff[0], stuff[2]);
				}
				//only updates currentType on initial emit
				this.noEmitTypeUpdate(editData.selectedType);

				return { editData, type: stuff[1] };
			}),
			switchMap((editCombined) => {
				const editEmpty$ = this.selectedTicketList.editTicket$.pipe(
					map((list) => list.size),
					filter((size) => size === 0),
					take(1)
				);

				return concat(
					of(editCombined.editData),
					this.selectedTicketList.editTicketsWithUpdate$.pipe(
						filter((action) => this.isEditing),
						bufferTime(20), //TODO - replace with custom buffer
						filter((buff) => buff.length > 0),
						switchMap((buff) => {
							//TODO - replace this getValue with a pairwise/ startWith operator
							// and maybe try and branch the changd/ not changed with a partition
							const oldCombined = this.combinedEdit$.getValue();
							const allInternal = buff.every((action) => action.type === TicketActionTypes.ticketEditUpdate);
							//this only works because references
							const currentEditTickets = this.selectedTicketList.editTicket$.getValue();
							const newCombined = new EditTicketsCombined(
								oldCombined.selectedType,
								currentEditTickets,
								this.persistentData$.getValue()
							);
							newCombined.lastUpdateInternal = allInternal;

							//typeUpdate maybe, only update when etc calculates something new, and don't emit that change
							const serviceKnownType = this.currentType$.getValue();
							if (newCombined.selectedType !== serviceKnownType) {
								this.noEmitTypeUpdate(newCombined.selectedType);
							}

							const changedKeys = EditTicketsCombined.combinedChanged(oldCombined, newCombined);
							//if thing has changed, todo, implement
							if (changedKeys !== null) {
								return of(newCombined);
							} else {
								// Logging.notice('no significant data changed, so no event');
								this.logger.log('no significant data changed, so no event');
								return EMPTY;
							}
						}),
						takeUntil(editEmpty$),
						endWith(EditTicketsCombined.EMPTY())
					)
				);
			}),
			tap((etc) => this.decorateSelectedList(etc)),
			tap((etc) => this.startOrEndSession(etc))
			// tap(comb => this.logger.log('tap combinedEdit$', comb))
			// takeUntil(this.shutdownSubject),
		);

		// MOC-3380
		this.planState.tickets.editTicket$.pipe(takeUntil(this.shutdownSubject)).subscribe((list) => {
			if (list && this.planState.scopeSoup && this.planState.scopeSoup.floatingRegionApi) {
				this.planState.scopeSoup.floatingRegionApi.hide();
			}
		});
		this.planState.floatingTickets.editTicket$.pipe(takeUntil(this.shutdownSubject)).subscribe((list) => {
			if (
				list &&
				this.planState.scopeSoup &&
				this.planState.scopeSoup.floatingRegionApi &&
				!this.planState.scopeSoup.floatingRegionApi.isShown()
			) {
				this.planState.scopeSoup.floatingRegionApi.show();
			}
		});

		//don't want to subscribe to the completion, because that will complete
		// a session length observable
		editOb$.pipe(takeUntil(this.shutdownSubject)).subscribe((comb) => {
			this.combinedEdit$.next(comb);
		});
		this.combinedEditForForm$ = this.combinedEdit$.pipe(
			filter((comb) => !comb.lastUpdateInternal),
			takeUntil(this.shutdownSubject)
		);

		// this.setupTemplateWatcher();
	} //generateEditWatcher()

	private startOrEndSession(etc: EditTicketsCombined) {
		//TODO for Kurits! try moving this into the emission instead of a tap pipe, see what happens
		if ((!etc && this.isEditing) || (etc && etc.originalList.size === 0 && this.isEditing)) {
			this.removeEdit();
		}
		if (!this.isEditing && etc && etc.originalList.size > 0) {
			this.openEdit(etc);
		}
	}

	private decorateSelectedList(etc: EditTicketsCombined | null) {
		if (!etc) {
			return;
		}
		etc.ticketsInPlanRegion = this.selectedTicketList === this.planState.tickets;
	}

	/**
	 * sets the edit overlay, eg, promisePin
	 * @param  type The EditOverlay type, null to remove
	 */
	public setOverlay(type?: EditOverlay) {
		this.editOverlay$.next(type);
	}

	/**
	 * For persisting data back to the ticketList
	 * @param  list [description]
	 * @param  data [description]
	 * @param applyChangeLocally setting this to true means that the edit observable will
	 * consider this change to be a significant change and reapply it to the model
	 * in the same way it would apply a data change
	 */
	private writeDataBackToList(list, data: any, applyChangeLocally = false) {
		if (!list.size) {
			this.logger.log('no list');
		}
		list.forEach((t) => {
			const subData = data && data.size ? data.get(t.$id) : data;
			if (applyChangeLocally) {
				this.planState.actions.updateTicketLiveData(t.$id, subData, this.selectedTicketList);
			} else {
				this.planState.actions.updateTicketEditData(t.$id, subData, this.selectedTicketList);
			}
		});
	}

	/**
	 * The main way to write data back to the ticket from edit
	 * @param  data                     Object representing the data to write
	 * @param  applyChangeLocally=false If true the change will be written as a normal
	 * ticket update and will be considering a "non-internal" change by the edit form
	 */
	public writeCombinationBackToList(combination: EditTicketsCombined, data: any, applyChangeLocally = false) {
		if (!data || typeof data !== 'object') {
			return;
		}

		//this is a short term hack
		function isEmpty(dat: any) {
			return dat === null || dat === undefined || dat === '';
		}

		function isSame(a, b) {
			return (isEmpty(a) && isEmpty(b)) || a === b;
		}

		if (combination.isMulti) {
			Object.keys(data).forEach((key) => {
				// only write the changed keys in multi-edit, this is to avoid overriting certain unmodified keys
				if (isSame(data[key], combination.data[key])) {
					delete data[key];
				}
			});
		}
		this.writeDataBackToList(combination.selectedList, data, applyChangeLocally);
	}

	//a special update that does not run through the "writeCombinationBackToList" flow
	// but instead updates each ticket more manually with per-ticket curated data
	//this currently works with multi because duration is not clearable
	public setDurationFromForm(combination: EditTicketsCombined, duration: number) {
		combination.selectedList.forEach((ticket) => {
			if (!ticket.view.liveStart || ticket.view.side === Side.PULL) {
				// Logging.warning('DEVTEXT - no date, what do');
				this.planState.actions.updateTicketEditData(
					ticket.$id,
					{ liveDurationDays: duration },
					this.selectedTicketList
				);
			} else if (duration != null && (duration as any) !== '') {
				const durationData = this.planState.calendarWorkdays.getMetadataForDuration(
					ticket.view.liveExceptionDays,
					ticket.view.roleId,
					ticket.view.liveStart,
					duration
				);
				this.planState.actions.updateTicketLiveData(ticket.$id, durationData, this.selectedTicketList);
			}
		});
	}

	public resetPreviousHudPosition(): void {
		this.previousHudPos = null;
	}

	public setDateRangeFromForm(
		combination: EditTicketsCombined,
		dateRange: { liveStart: string | null; liveFinish: string | null }
	) {
		const roleId = combination.data.roleId; //might be null in multiedit case
		if (combination.databaseSide === Side.PULL) {
			return;
		}

		if (!dateRange) {
			this.logger.log('DEVTEXT - no range, what do');
			return;
		}

		// get left
		const dateRangeData = Object.assign(
			// liveExceptionDays is not used for the calculation of isWorkday on the liveStart & liveFinish dates
			this.planState.calendarWorkdays.getMetadataForDateRange(
				combination.data.liveExceptionDays,
				roleId,
				dateRange.liveStart,
				dateRange.liveFinish
			),
			{
				left: this.planState.activeColumnService.guessXFromDate(dateRange.liveStart, 1),
			}
		);

		this.writeCombinationBackToList(combination, dateRangeData, true);
	}

	private getWorkDaysData(combination: EditTicketsCombined, workdays: boolean[], roleId?: string) {
		if (!workdays) {
			this.logger.log('DEVTEXT - no days, what do');
			return;
		}
		//if the first one is off, just flip it back on
		if (workdays.length && !workdays[0]) {
			Logging.notice(
				combination.data.liveDurationDays === 1
					? strings.WARNING_SET_START_NONWORK_1DAY
					: strings.WARNING_SET_START_NONWORK
			);
		}

		const persistentData = this.persistentData$.getValue();
		const workdaysData = this.planState.calendarWorkdays.getMetadataForWorkdays(
			workdays,
			combination.data.liveExceptionDays,
			combination.data.liveDurationDays,
			roleId ?? combination.data.roleId,
			combination.data.liveStart,
			false
		);

		return workdaysData;
	}

	public setWorkdaysFromForm(combination: EditTicketsCombined, workdays: boolean[]) {
		if (combination.data.side === Side.PULL) {
			return;
		}

		const workdaysData = this.getWorkDaysData(combination, workdays);

		this.writeCombinationBackToList(combination, workdaysData, true);
		this.workdays$.next(workdaysData.liveWorkingDays);
	}

	//a special update that does not run through the "writeCombinationBackToList" flow
	// but instead updates each ticket more manually with per-ticket curated data
	//this currently works with multi because role is not clearable
	public setRoleIdFromForm(combination: EditTicketsCombined, roleId: string) {
		combination.selectedList.forEach((ticket) => {
			let newData;
			if (ticket.view.side !== Side.PULL) {
				const start = ticket.view.liveStart;
				// Recalculating everything according to the start date and the current duration of the ticket without exception days (kind of resetting them, see the getMetadataForDuration code for details)
				// This is not recalculating the duration, but it is recalculating other properties such as workdays, finish date, etc.
				const durationMetadata = this.planState.calendarWorkdays.getMetadataForDuration(
					undefined,
					roleId,
					start,
					ticket.view.liveDurationDays
				);
				newData = durationMetadata;
			}
			this.planState.actions.updateTicketEditData(
				ticket.$id,
				{ roleId, ...newData },
				this.selectedTicketList
			);
		});
	}

	public setCollectedVariance(
		combination: EditTicketsCombined,
		varianceId?: string,
		varianceComment?: string
	) {
		//completely bypass etc since this needs to operate on each ticket's data independently
		combination.selectedList.forEach((t) => {
			if (t.view.liveLastPromise) {
				const newPin = applyVarianceToPin(t.view.liveLastPromise, varianceId, varianceComment);
				const wrapped = { liveLastPromise: newPin };
				this.planState.actions.updateTicketEditData(t.$id, wrapped, this.selectedTicketList);
			}
		});
	}

	/** special write that bypasses the changed check so you can clear the input from multi */
	public clearInput(list: Map<string, Ticket>, key: string) {
		this.writeDataBackToList(list, { [key]: null }, false);
	}

	//don't need to do anything fancy here, if all the tickets in the edit are deleted, then edit is closed
	// yay single direction flow of data
	public deleteEditTickets(tickets?: Map<string, Ticket>) {
		if (!tickets) {
			tickets = this.combinedEdit$.getValue().selectedList;
		}

		const config = {
			disableConfirmation: tickets.size <= 1,
			ticketListOverride: this.selectedTicketList,
		};

		this.validatedPlanActions.tryDeleteTickets(tickets, config);
		// this.planState.actions.deleteTicket(tickets);
	}

	public copyTicket() {
		const ticketAndId = this.combinedEdit$.getValue().selectedList;
		clipboardManager.copySingleTicket(ticketAndId);
	}

	public duplicateEditTickets(tickets?: Map<string, Ticket>) {
		if (!tickets) {
			tickets = this.combinedEdit$.getValue().selectedList;
		}

		if (!this.isEditValidSync) {
			this.markFormAsDirty(false);
			Logging.warning(strings.EDIT_TOP_BAR_TICKET_ERRORS_TO_SAVE);
			return;
		}

		this.logImplicitSave();
		this.saveEdit(tickets);

		let config: any = { selectImmediately: true, editImmediately: true };

		//special override for floating tickets until it's migrated
		//just close the entirity of edit
		if (this.selectedTicketList === this.planState.floatingTickets) {
			config = {};
			const allTickets = this.combinedEdit$.getValue().originalList;
			this.planState.actions.clearSelection(allTickets, this.selectedTicketList);
			this.planState.actions.changeTicketViewToDefault(allTickets, this.selectedTicketList);
		}
		//normal flow
		else {
			this.planState.actions.clearSelection(tickets, this.selectedTicketList);
			this.planState.actions.changeTicketViewToDefault(tickets, this.selectedTicketList);
		}

		this.planState.actions.duplicateTicket(tickets, config, this.selectedTicketList);
	}

	public toggleEditTimeline(tickets?: Map<string, Ticket>) {
		if (!tickets) {
			tickets = this.combinedEdit$.getValue().selectedList;
		}
		tickets.forEach((t) => {
			let ticketId = t.$id;
			let planId = this.planState.planId;
			//this is for possible future functionality where we might support editing tickets in the timeline list
			if (t.isTimelineTicket) {
				planId = t.timelineTicketSplitId[0];
				ticketId = t.timelineTicketSplitId[1];
			}
			if (!planId) {
				this.logger.log('no planId?');
				return;
			}

			const isOnNow = this.planState.legacyActions.toggleTimelineTicket(planId, ticketId);

			//temp workaround for removing from timeline if cancelling
			//because this is not action based, nothing will know about this change
			t.wasJustAddedToTimeline = isOnNow;
		});
	}

	public showTicketAttachments(): void {
		this.ticketEditPanelIsDisplayed$.next(true);
	}

	public hideTicketAttachments(): void {
		this.ticketEditPanelIsDisplayed$.next(false);
	}

	/**
	 * Returns an Observable of Ticket objects, where each non-null Ticket is for
	 * a single-selection Ticket that is being edited.  In a multi-select edit,
	 * nothing is yielded.
	 */
	public getSingleEditTicket$(): Observable<Ticket> {
		return this.planState.tickets.editTicket$.pipe(
			debounceTime(10),
			filter((ticketMap) => ticketMap.size === 1),
			map((ticketMap) => {
				for (const value of ticketMap.values()) {
					return value;
				}
			})
		);
	}

	/**
	 * Adds pins to the passed in tickets, skipping tickets that cannot have a pin
	 */
	public addEditPin(tickets?: Map<string, Ticket>) {
		if (!tickets) {
			tickets = this.combinedEdit$.getValue().selectedList;
		}
		tickets.forEach((t) => {
			if (!Ticket.canAddPin(t.view)) {
				return;
			}
			const data = createInitialPinData(t.view.liveStart, t.view.liveFinish);
			this.planState.actions.addPinToTicket(t, data);
			analyticsEventsService.promisePinAddedToTicket(t.$id, this.planState.planId);
		});
	}

	/**
	 * Removes a pin from each of the passed in tickets, skipping tickets that cannot have a pin removed
	 */
	public removeEditPin(tickets?: Map<string, Ticket>) {
		if (!tickets) {
			tickets = this.combinedEdit$.getValue().selectedList;
		}
		tickets.forEach((t) => {
			if (!Ticket.canRemovePin(t.view)) {
				return;
			}
			this.planState.actions.removePinFromTicket(t);
			analyticsEventsService.promisePinRemovedFromTicket(t.$id, this.planState.planId);
		});
	}
	//TODO - either make this private, or create a public method shorthand like closeEdit
	public openEdit(etc: EditTicketsCombined) {
		// this.ticketEditServiceTemplater.openEdit(component);

		// MOC-4046 - Special case constraint side panel
		if (this.planState.legacyActions.isConstraintSidePanelClean) {
			this.planState.legacyActions.closeConstraintPanel();
		}
		this.isEditing = true;
	}
	//technically this just does the same thing as openEdit at the moment
	public changeEdit(component: any) {
		// this.ticketEditServiceTemplater.closeEdit();
		this.ticketEditServiceTemplater.openEdit(component);
	}

	public saveEdit(tickets?: Map<string, Ticket>) {
		if (!tickets) {
			tickets = this.combinedEdit$.getValue().selectedList;
		}

		this.closeMethod$.next('save');
		//this only flies because the ticket is being persisted right after
		tickets.forEach((t) => delete t.ticketJustAdded);

		this.planState.actions.persistTicketData(tickets, this.selectedTicketList);

		tickets.forEach((t) => {
			analyticsEventsService.editedATicket(
				t.$id,
				this.planState.planId,
				this.planAccessCheck.hasRestrictedAccessSync,
				t.view.liveLastPromise && t.view.liveLastPromise.data.plannedFinish
					? t.view.liveLastPromise.data.plannedFinish
					: null,
				this.selectedTicketList === this.planState.floatingTickets
			);
		});
	}

	//shorthand
	public saveEditAndClose(tickets?: Map<string, Ticket>) {
		if (!tickets) {
			if (!this.combinedEdit$.getValue()) {
				return;
			} //just do nothing if edit isn't open
			tickets = this.combinedEdit$.getValue().selectedList;
		}
		this.saveEdit(tickets);
		this.closeEdit();
	}

	/**
	 * To be called by the ui as shorthand for setting all tickets set to edit view back to default.
	 * As a result of that, the edit ui will close.
	 */
	public closeEdit() {
		const syncEditTickets = this.combinedEdit$.getValue();
		if (!syncEditTickets) {
			this.logger.log('nothing to close');
			return;
		}

		this.persistentData$.next(this.resetPersistentData());
		this.planState.actions.abortEdit();

		this.hideTicketAttachments();
	}

	/** internal function to remove the template, called by the observable */
	private removeEdit() {
		// this.ticketEditServiceTemplater.closeEdit();
		this.isEditing = false;

		// note: editFormDirty$ is subscribed in undo ticket-edit-operation
		// we currently rely on ticket edit window close & form dirty to capture the "after state" data for undo
		this.checkEditFormDirty();
		this.editClosedSubject.next(true);
		this.setCurrentType(TicketType.Task);
	}

	/** use as default for writes */
	private getGlobalEditState() {
		return this.combinedEdit$.getValue();
	}

	//state transition section

	public transitionToActualizing(etc?: EditTicketsCombined) {
		if (!etc) {
			etc = this.getGlobalEditState();
		}
		//can assume that etc is already persisted to edit data
		if (etc.data.actualPhase === ActualPhase.planned || etc.data.actualPhase === ActualPhase.started) {
			etc.selectedList.forEach((t) => {
				const newData = this.convertPlannedToActual(t);
				newData.actualPhaseOverride = ActualPhase.actualizing;
				this.planState.actions.updateTicketLiveData(t.$id, newData);
			});
		} else if (etc.data.actualPhase === ActualPhase.actualized) {
			this.writeCombinationBackToList(etc, { actualPhaseOverride: ActualPhase.actualizing }, true);
		} else if (etc.data.actualPhase === ActualPhase.plannedPreview) {
			etc.selectedList.forEach((t) => {
				const newData = this.swapActualPlanned(t, false);
				newData.actualPhaseOverride = ActualPhase.actualizing;
				this.planState.actions.updateTicketLiveData(t.$id, newData);
			});
		} else {
			this.logger.log('what is this', etc.data.actualPhase, etc);
		}
	}

	public transitionToActualized(etc?: EditTicketsCombined) {
		if (etc.data.actualPhase === ActualPhase.actualizing) {
			this.writeCombinationBackToList(etc, { actualPhaseOverride: null }, true);
		} else if (etc.data.actualPhase === ActualPhase.plannedPreview) {
			etc.selectedList.forEach((t) => {
				const newData = this.swapActualPlanned(t, false);
				newData.actualPhaseOverride = null;
				this.planState.actions.updateTicketLiveData(t.$id, newData);
			});
		} else {
			this.logger.log('what is this', etc.data.actualPhase, etc);
		}
	}

	public transitionToPlanned(etc?: EditTicketsCombined) {
		if (!etc) {
			etc = this.getGlobalEditState();
		}
		//if(etc.data.actualPhase === ActualPhase.actualizing)
		//can come here from any state (except self)

		if (etc.data.actualPhase === ActualPhase.planned) {
			return;
		}

		// if(etc.data.actualPhase === ActualPhase.plannedPreview){
		// 	etc.selectedList.forEach(t => {
		// 		let newData = this.revertActualToPlanned(t);
		// 		newData.actualPhaseOverride = null;
		// 		this.planState.actions.updateTicketLiveData(t.$id, newData);
		// 	});
		// 	return;
		// }

		if (etc.data.actualPhase === ActualPhase.started) {
			etc.selectedList.forEach((t) => {
				let newData = this.revertStartedToPlanned();
				this.planState.actions.updateTicketLiveData(t.$id, newData);
			});
			return;
		}

		etc.selectedList.forEach((t) => {
			const newData = this.revertActualToPlanned(t);
			if (etc.data.actualPhase === ActualPhase.plannedPreview) {
				newData.actualPhaseOverride = null;
			}
			this.planState.actions.updateTicketLiveData(t.$id, newData);
			// this.planState.actions.updateTicketEditData(t.$id, newData);
		});
	}

	public transitionToStarted(etc?: EditTicketsCombined) {
		if (!etc) {
			etc = this.getGlobalEditState();
		}
		if (etc.data.actualPhase !== ActualPhase.planned) {
			return;
		}
		etc.selectedList.forEach((ticket) => {
			const newData = ticket.convertPlannedToStarted();
			this.planState.actions.updateTicketLiveData(ticket.$id, newData);
			analyticsEventsService.startWork(this.planState.planId, ticket.$id);
		});
	}

	public transitionToPlannedPreview(etc?: EditTicketsCombined) {
		if (!etc) {
			etc = this.getGlobalEditState();
		}
		etc.selectedList.forEach((t) => {
			const newData = this.swapActualPlanned(t, true);
			newData.actualPhaseOverride = ActualPhase.plannedPreview;
			this.planState.actions.updateTicketLiveData(t.$id, newData);
		});
	}

	//state transition helpers

	public convertPlannedToActual(t: Ticket) {
		const newData: any = {};

		Object.keys(plannedOnly).forEach((plannedKey) => {
			const plannedKeyData = plannedOnly[plannedKey];

			newData[plannedKeyData.lastPlannedKey] = t.view[plannedKey];
			newData[plannedKeyData.actualKey] = t.view[plannedKey];
		});
		if (t.view.liveLastPromise) {
			const newPin = clonePin(t.view.liveLastPromise);
			newPin.data.actualFinish = newData.actualFinish;
			newData.liveLastPromise = newPin;

			// let newStack = pinUtils.cloneStack(t.view.livePromisePeriodStack);
			// newStack[newStack.length-1].data.actualFinish = newData.actualFinish;
			// newData.livePromisePeriodStack = newStack;
		}
		// MOC-4354 - Special override to prevent constraint from displaying broken data when completed before accepted
		if (t.view.assignedProjectMemberId) {
			newData['responsibleProjectMemberId'] = t.view.assignedProjectMemberId;
			newData['responsibleProjectMemberRoleId'] = t.view.assignedProjectMemberRoleId;
			newData['assignedProjectMemberId'] = null;
			newData['assignedProjectMemberRoleId'] = null;
		}
		return newData;
	}

	public revertActualToPlanned(t: Ticket) {
		const newData: any = {};

		Object.keys(plannedOnly).forEach((plannedKey) => {
			const plannedKeyData = plannedOnly[plannedKey];
			newData[plannedKey] = t.view[plannedKeyData.lastPlannedKey];
			newData[plannedKeyData.actualKey] = null;
			newData[plannedKeyData.lastPlannedKey] = null;
		});
		if (t.view.liveLastPromise) {
			const newPin = clonePin(t.view.liveLastPromise);
			delete newPin.data.actualFinish;
			// newPin.data.actualFinish = newData.actualFinish;
			newData.liveLastPromise = newPin;
		}

		return newData;
	}

	public revertStartedToPlanned() {
		return { actualStart: null };
	}

	//final api should be a new ticket action, this should be a method on the ticket
	// the method should be more of a "refresh", it changes the actual association and recalcs all the live values
	public swapActualPlanned(t: Ticket, toPlanned = true) {
		const newData: any = {};
		// if(toPlanned){
		// 	Object.keys(TicketAttributeConfig.plannedOnly).forEach(plannedKey => {
		// 		let plannedKeyData = TicketAttributeConfig.plannedOnly[plannedKey];
		// 		newData[plannedKeyData.localKeyOverride] = t.view[plannedKey];
		// 	});
		// }
		// else{
		// 	Object.keys(TicketAttributeConfig.plannedOnly).forEach(plannedKey => {
		// 		let plannedKeyData = TicketAttributeConfig.plannedOnly[plannedKey];
		// 		newData[plannedKeyData.localKeyOverride] = t.view[plannedKeyData.actualKey];
		// 	});
		// }

		return newData;
	}

	public setCurrentType(type: CombinedTicketTypes) {
		this.currentType$.next(type);
	}

	public switchTypeFromUi(type: CombinedTicketTypes, tickets?: Map<string, Ticket>) {
		if (!this.isEditValidSync) {
			Logging.warning(strings.TICKET_ERRORS_EDIT_INVALID_SWITCH_TICKET);
			return;
		}
		this.logImplicitSave();
		this.saveEdit(tickets);
		this.setCurrentType(type);
	}

	public logImplicitSave() {
		if (this.isFormDirty()) {
			Logging.notice(strings.TICKET_SAVED_IMPLICITLY);
		}
	}

	// public focusTicket(combination: EditTicketsCombined, z?: number): void {
	// 	const camera = this.planState.canvasConnector.camera;
	// 	const screenRect = camera.getScreen();
	// 	screenRect.height -= 2 * VERT_PIXEL_OFFSET_FROM_CENTER_ON_TICKET_EDIT_FOCUS;
	// 	camera.focusOnTicketsFromEdit(
	// 		combination,
	// 		z,
	// 		0, // VERT_PIXEL_OFFSET_FROM_CENTER_ON_TICKET_EDIT_FOCUS,
	// 		screenRect
	// 	);
	// }

	public focusTicket(combination: EditTicketsCombined, z?: number): void {
		const camera = this.planState.canvasConnector.camera;
		// BEGIN PLACEHOLDER REPLACE AS PART OF TP-6565
		// this makes it not blow up, but also not zoom
		if (!camera) {
			this.logger.log('camera loaded too slow to zoom');
			return;
		}
		// END PLACEHOLDER REPLACE AS PART OF TP-6565
		camera.focusOnTicketsFromEdit(combination, z, VERT_PIXEL_OFFSET_FROM_CENTER_ON_TICKET_EDIT_FOCUS);
	}

	public openMoreDetailsFromEdit(etc: EditTicketsCombined) {
		const canClose: boolean = this.resolveOpenEdit(strings.TICKET_ERRORS_EDIT_INVALID_MORE_DETAILS);

		if (canClose) {
			const assembledId: string = this.planState.planId + ',' + etc.data.id;
			this.planState.legacyActions.openConstraintNotePanel(assembledId);
			this.planState.legacyActions.markConstraintSidePanelClean();
		} else {
			this.markFormAsDirty(false);
			etc.setMessageString(strings.TICKET_ERRORS_EDIT_INVALID_MORE_DETAILS);
		}
	}

	public registerForm(editForm: UntypedFormGroup): void {
		this._editForm = editForm;
		//isValid$ handles it's own clean up
		const isValid$ = defer(() =>
			this._editForm.statusChanges.pipe(
				map((status) => status !== 'INVALID'),
				startWith(this._editForm.status !== 'INVALID'),
				takeUntil(this._editformKilled),
				endWith(true)
			)
		);

		//one subscription for editService
		isValid$.subscribe((valid) => {
			this.isEditValid$.next(valid);
		});

		//one for the interaction-blocker
		this.planState.interactionBlocker.registerIsEditValid(isValid$);
	}

	public clearForm(): void {
		this._editformKilled.next(true);
		this._editForm = null;
	}

	public markFormAsDirty(onlySelf = true): void {
		if (!this._editForm) {
			return;
		}
		const options = { onlySelf };

		//manually do the thing onlySelf _should_ be doing
		if (!onlySelf) {
			// tslint:disable-next-line: forin
			for (const key in this._editForm.controls) {
				this._editForm.controls[key].markAsDirty();
			}
		}
		this._editForm.markAsDirty(options);
	}

	public isFormDirty(): boolean {
		if (!this._editForm) {
			return false;
		}
		return this._editForm.dirty;
	}

	// added for edit undo
	public checkEditFormDirty(): void {
		// this._editForm could be null if tickets are cycled too fast
		const isDirty = this.isFormDirty();
		this.closeEditAndCheckDirty$.next(isDirty);
	}

	public getEditFormControl(id: string): AbstractControl {
		return this._editForm.get(id);
	}

	public resolveOpenEdit(errorMessage: string = strings.TICKET_ERRORS_EDIT_INVALID_WHEN_ADDING): boolean {
		if (!this.isEditValidSync) {
			Logging.warning(errorMessage);
			return false;
		}
		this.logImplicitSave();
		this.saveEditAndClose();
		return true;
	}
}
